import { getCroppedImg } from '@/utils/cropImage';
import { handleError } from '@/utils/handleError';
import { useState } from 'react';
import Cropper, { Area } from 'react-easy-crop';
import { useTranslation } from 'react-i18next';
import { LuZoomIn, LuZoomOut } from 'react-icons/lu';
import { Button } from './Button';
import { LineSeparator } from './LineSeparator';
import { Modal } from './Modal';

interface CropperComponentProps {
  imageSrc: string;
  ratio?: number;
  cropShape?: 'rect' | 'round';
  showGrid?: boolean;
  onCropIsComplete: (croppedImage: File) => void;
  onClose?: () => void;
  isOpen: boolean;
  isRequired?: boolean;
}

export const ImageCropper = ({
  imageSrc,
  ratio = 1, // Ratio par défaut de 1 (carré)
  cropShape = 'rect',
  showGrid = true,
  onCropIsComplete,
  onClose = () => {},
  isOpen,
  isRequired = false,
}: CropperComponentProps) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const { t } = useTranslation();

  const handleZoomIn = () => {
    setZoom(prev => Math.min(10, prev + 0.2));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(1, prev - 0.2));
  };

  const handleCropComplete = async () => {
    if (!croppedAreaPixels) return;

    try {
      const croppedImageBlob = await getCroppedImg(imageSrc, croppedAreaPixels);

      const file = new File([croppedImageBlob], 'cropped-image.png', {
        type: 'image/png',
      });
      onCropIsComplete(file);
      onClose();
    } catch (error) {
      handleError(error);
    }
  };

  return (
    <Modal
      onClose={onClose}
      isOpen={isOpen}
      className="max-h-screen w-full max-w-2xl"
    >
      <div className="relative flex h-96 w-full items-center justify-between p-4">
        <div
          className="absolute inset-0 flex items-center justify-center"
          style={{
            width: '100%',
            height: '100%',
          }}
        >
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={ratio}
            onCropChange={setCrop}
            onCropComplete={(_, croppedAreaPixels) => {
              setCroppedAreaPixels(croppedAreaPixels);
            }}
            onZoomChange={setZoom}
            cropShape={cropShape}
            showGrid={showGrid}
            maxZoom={10}
            objectFit="contain"
            restrictPosition={true}
            style={{
              containerStyle: {
                width: '100%',
                height: '100%',
                backgroundColor: '#333',
              },
            }}
          />
        </div>
      </div>

      <div className="flex h-20 items-start justify-center space-x-4 p-4">
        <Button
          className="bg-primary"
          onClick={handleZoomOut}
          disabled={zoom <= 1}
        >
          <LuZoomOut className="text-secondary" />
        </Button>
        <Button
          className="bg-primary"
          onClick={handleZoomIn}
          disabled={zoom >= 10}
        >
          <LuZoomIn className="text-secondary" />
        </Button>
      </div>

      <LineSeparator className="mt-2" />

      <div className="flex items-center justify-center space-x-4 md:justify-end">
        {!isRequired && (
          <Button onClick={onClose} className="bg-danger">
            {t('imageCropper:cancel')}
          </Button>
        )}
        <Button onClick={handleCropComplete}>
          {t('imageCropper:validate')}
        </Button>
      </div>
    </Modal>
  );
};
